import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const AuthorizationIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const AuthorizationCodeSchema = Type.String({
  minLength: 1,
  maxLength: 80,
  pattern: "^[A-Z][A-Z0-9_]{0,79}$",
});
const AuthorizationChineseTextSchema = Type.String({
  minLength: 2,
  maxLength: 500,
  pattern: "[\\u3400-\\u9FFF]",
});
const AuthorizationNullableVersionSchema = Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]);
const AuthorizationNullableSha256Schema = Type.Union([
  Type.String({ pattern: "^[a-f0-9]{64}$" }),
  Type.Null(),
]);

export const AuthorizationPrincipalTypeSchema = Type.Union([
  Type.Literal("WORKFORCE"),
  Type.Literal("CUSTOMER"),
  Type.Literal("KIOSK"),
  Type.Literal("SUPPORT"),
  Type.Literal("SERVICE"),
]);
export type AuthorizationPrincipalType = Static<typeof AuthorizationPrincipalTypeSchema>;

export const AuthorizationEffectSchema = Type.Union([
  Type.Literal("DIRECT"),
  Type.Literal("MANAGER_OR_OWNER_APPROVAL"),
  Type.Literal("MANAGER_THEN_OWNER_APPROVAL"),
  Type.Literal("DENY"),
]);
export type AuthorizationEffect = Static<typeof AuthorizationEffectSchema>;

export const AuthorizationPrincipalSchema = Type.Object(
  {
    type: AuthorizationPrincipalTypeSchema,
    id: AuthorizationIdSchema,
  },
  { additionalProperties: false },
);
export type AuthorizationPrincipal = Static<typeof AuthorizationPrincipalSchema>;

export const AuthorizationScopeSchema = Type.Object(
  {
    tenantId: AuthorizationIdSchema,
    storeId: Type.Union([AuthorizationIdSchema, Type.Null()]),
    resourceId: Type.Union([AuthorizationIdSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type AuthorizationScope = Static<typeof AuthorizationScopeSchema>;

export const AuthorizationConditionValueSchema = Type.Union([
  Type.Boolean(),
  Type.Integer(),
  Type.String({ minLength: 1, maxLength: 256 }),
  Type.Null(),
]);
export type AuthorizationConditionValue = Static<typeof AuthorizationConditionValueSchema>;

export const AuthorizationConditionSchema = Type.Object(
  {
    code: AuthorizationCodeSchema,
    value: AuthorizationConditionValueSchema,
  },
  { additionalProperties: false },
);
export type AuthorizationCondition = Static<typeof AuthorizationConditionSchema>;

export const AuthorizationPolicyConditionSchema = Type.Object(
  {
    source: Type.Union([
      Type.Literal("TRUSTED_FACT"),
      Type.Literal("PRINCIPAL"),
      Type.Literal("POLICY"),
    ]),
    code: AuthorizationCodeSchema,
    operator: Type.Union([
      Type.Literal("EQUALS"),
      Type.Literal("INCLUDES_ANY"),
      Type.Literal("MATCHES_PRINCIPAL"),
      Type.Literal("ACTIVE_AND_VERSIONED"),
    ]),
    value: AuthorizationConditionValueSchema,
  },
  { additionalProperties: false },
);
export type AuthorizationPolicyCondition = Static<typeof AuthorizationPolicyConditionSchema>;

const AuthorizationVersionFields = {
  policyVersion: Type.Integer({ minimum: 1 }),
  policyContentSha256: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  projectionVersion: AuthorizationNullableVersionSchema,
  identityVersion: AuthorizationNullableVersionSchema,
  bindingVersion: AuthorizationNullableVersionSchema,
  projectionHash: AuthorizationNullableSha256Schema,
  authorizationFactHash: AuthorizationNullableSha256Schema,
};

export const AuthorizationRequestSchema = Type.Object(
  {
    requestId: AuthorizationIdSchema,
    principal: AuthorizationPrincipalSchema,
    scope: AuthorizationScopeSchema,
    resource: AuthorizationCodeSchema,
    action: AuthorizationCodeSchema,
    conditions: Type.Array(AuthorizationConditionSchema, {
      maxItems: 64,
      uniqueItems: true,
    }),
    ...AuthorizationVersionFields,
    requestedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type AuthorizationRequest = Static<typeof AuthorizationRequestSchema>;

export const AuthorizationMatchedBySchema = Type.Union([
  Type.Object(
    {
      kind: Type.Literal("WORKFORCE_ROLE"),
      principalType: Type.Literal("WORKFORCE"),
      role: Type.Union([
        Type.Literal("OWNER"),
        Type.Literal("MANAGER"),
        Type.Literal("CASHIER"),
        Type.Literal("WAITER"),
        Type.Literal("COACH"),
      ]),
      referenceId: Type.Null(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("SUPPORT_GRANT"),
      principalType: Type.Literal("SUPPORT"),
      role: Type.Null(),
      referenceId: AuthorizationIdSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("PRINCIPAL_SCOPE"),
      principalType: Type.Union([
        Type.Literal("CUSTOMER"),
        Type.Literal("KIOSK"),
        Type.Literal("SERVICE"),
      ]),
      role: Type.Null(),
      referenceId: AuthorizationIdSchema,
    },
    { additionalProperties: false },
  ),
]);
export type AuthorizationMatchedBy = Static<typeof AuthorizationMatchedBySchema>;

const AuthorizationDecisionCommon = {
  requestId: AuthorizationIdSchema,
  message: AuthorizationChineseTextSchema,
  nextStep: AuthorizationChineseTextSchema,
  scope: AuthorizationScopeSchema,
  resource: AuthorizationCodeSchema,
  action: AuthorizationCodeSchema,
  ...AuthorizationVersionFields,
  decidedAt: Type.String({ format: "date-time" }),
};

export const AuthorizationDecisionSchema = Type.Union([
  Type.Object(
    {
      ...AuthorizationDecisionCommon,
      effect: Type.Literal("DIRECT"),
      reasonCode: Type.Literal("AUTHORIZATION_DIRECT"),
      matchedRuleId: AuthorizationIdSchema,
      matchedBy: AuthorizationMatchedBySchema,
      canExecute: Type.Literal(true),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...AuthorizationDecisionCommon,
      effect: Type.Literal("MANAGER_OR_OWNER_APPROVAL"),
      reasonCode: Type.Literal("AUTHORIZATION_MANAGER_OR_OWNER_APPROVAL_REQUIRED"),
      matchedRuleId: AuthorizationIdSchema,
      matchedBy: AuthorizationMatchedBySchema,
      canExecute: Type.Literal(false),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...AuthorizationDecisionCommon,
      effect: Type.Literal("MANAGER_THEN_OWNER_APPROVAL"),
      reasonCode: Type.Literal("AUTHORIZATION_MANAGER_THEN_OWNER_APPROVAL_REQUIRED"),
      matchedRuleId: AuthorizationIdSchema,
      matchedBy: AuthorizationMatchedBySchema,
      canExecute: Type.Literal(false),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...AuthorizationDecisionCommon,
      effect: Type.Literal("DENY"),
      reasonCode: Type.String({
        minLength: 15,
        maxLength: 94,
        pattern:
          "^AUTHORIZATION_(?!(?:DIRECT|MANAGER_OR_OWNER_APPROVAL_REQUIRED|MANAGER_THEN_OWNER_APPROVAL_REQUIRED)$)[A-Z0-9_]{1,80}$",
      }),
      matchedRuleId: Type.Union([AuthorizationIdSchema, Type.Null()]),
      matchedBy: Type.Union([AuthorizationMatchedBySchema, Type.Null()]),
      canExecute: Type.Literal(false),
    },
    { additionalProperties: false },
  ),
]);
export type AuthorizationDecision = Static<typeof AuthorizationDecisionSchema>;

const AuthorizationPolicyRuleCommon = {
  ruleId: AuthorizationIdSchema,
  principalTypes: Type.Array(AuthorizationPrincipalTypeSchema, {
    minItems: 1,
    maxItems: 5,
    uniqueItems: true,
  }),
  resource: AuthorizationCodeSchema,
  action: AuthorizationCodeSchema,
  priority: Type.Integer({ minimum: 0, maximum: 10_000 }),
  conditions: Type.Array(AuthorizationPolicyConditionSchema, {
    maxItems: 64,
    uniqueItems: true,
  }),
  message: AuthorizationChineseTextSchema,
  nextStep: AuthorizationChineseTextSchema,
};

export const AuthorizationPolicyRuleSchema = Type.Union([
  Type.Object(
    {
      ...AuthorizationPolicyRuleCommon,
      effect: Type.Literal("DIRECT"),
      reasonCode: Type.Literal("AUTHORIZATION_DIRECT"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...AuthorizationPolicyRuleCommon,
      effect: Type.Literal("MANAGER_OR_OWNER_APPROVAL"),
      reasonCode: Type.Literal("AUTHORIZATION_MANAGER_OR_OWNER_APPROVAL_REQUIRED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...AuthorizationPolicyRuleCommon,
      effect: Type.Literal("MANAGER_THEN_OWNER_APPROVAL"),
      reasonCode: Type.Literal("AUTHORIZATION_MANAGER_THEN_OWNER_APPROVAL_REQUIRED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...AuthorizationPolicyRuleCommon,
      effect: Type.Literal("DENY"),
      reasonCode: Type.String({
        minLength: 15,
        maxLength: 94,
        pattern:
          "^AUTHORIZATION_(?!(?:DIRECT|MANAGER_OR_OWNER_APPROVAL_REQUIRED|MANAGER_THEN_OWNER_APPROVAL_REQUIRED)$)[A-Z0-9_]{1,80}$",
      }),
    },
    { additionalProperties: false },
  ),
]);
export type AuthorizationPolicyRule = Static<typeof AuthorizationPolicyRuleSchema>;

export const AuthorizationPolicySnapshotSchema = Type.Object(
  {
    policyId: AuthorizationIdSchema,
    scope: AuthorizationScopeSchema,
    ...AuthorizationVersionFields,
    conditionSource: Type.Literal("SERVER_TRUSTED_ONLY"),
    replayMode: Type.Literal("VERSIONED_DOMAIN_RUNTIME"),
    defaultEffect: Type.Literal("DENY"),
    evaluationOrder: Type.Tuple([
      Type.Literal("TRUST_BOUNDARY"),
      Type.Literal("HARD_CONSTRAINT"),
      Type.Literal("IDENTITY_AND_SCOPE"),
      Type.Literal("EXPLICIT_RULE"),
      Type.Literal("DEFAULT_DENY"),
    ]),
    rules: Type.Array(AuthorizationPolicyRuleSchema, {
      maxItems: 2_000,
      uniqueItems: true,
    }),
    generatedAt: Type.String({ format: "date-time" }),
  },
  { additionalProperties: false },
);
export type AuthorizationPolicySnapshot = Static<typeof AuthorizationPolicySnapshotSchema>;

export function isAuthorizationRequest(value: unknown): value is AuthorizationRequest {
  return Value.Check(AuthorizationRequestSchema, value);
}

export function isAuthorizationDecision(value: unknown): value is AuthorizationDecision {
  return Value.Check(AuthorizationDecisionSchema, value);
}

export function isAuthorizationPolicySnapshot(
  value: unknown,
): value is AuthorizationPolicySnapshot {
  return Value.Check(AuthorizationPolicySnapshotSchema, value);
}
